import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { REPLY_PRESETS } from "@wodiary/shared";
import { api, ApiError } from "../api/client.js";
import {
  useEntry,
  useEntryAttachments,
  usePairing,
  useReplies,
} from "../api/queries.js";
import { useAuth } from "../auth/AuthContext.js";
import { errorMessage } from "../lib/errors.js";
import { formatDateTime, formatTime } from "../lib/format.js";
import {
  canDeleteReply,
  canReadReplies,
  canReply,
  isMine,
  showsReadReceipt,
} from "../lib/entries.js";
import { NavBar } from "../components/NavBar.js";
import { Avatar } from "../components/Avatar.js";
import { Loading, QueryError } from "../components/AsyncState.js";
import { LongBody } from "../components/LongBody.js";
import { VisibilityPrivateModal } from "../components/VisibilityPrivateModal.js";
import { useToast } from "../components/ToastContext.js";
import {
  CheckIcon,
  LockIcon,
  MoreIcon,
  PencilIcon,
  ReplyIcon,
  SendIcon,
  ShareIcon,
  TrashIcon,
} from "../components/icons.js";

/**
 * 日记详情（spec §5.4.2）。
 *
 * 两件「此刻发生」的事：
 * 1. **已读回执**：我读对方的共享日记时打一次 `read`（只记第一次）。作者读
 *    自己的日记不产生回执 —— 那是「我读了我写的」。
 * 2. **可见性迁移**：作者可以把 shared 改回 private（二次确认），或反过来。
 *
 * 「回应」与「已读」在界面上**分成两个区域**（spec §5.4.4 AC）——
 * 合并成一个标签会把「已读未回」视觉化，而那正是设计要避开的。
 */
export function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  const entryQuery = useEntry(id);
  const entry = entryQuery.data;
  const pairing = usePairing();
  const partner = pairing.data?.partnerUsername ?? "TA";

  const meId = user?.id ?? "";
  const mine = entry !== undefined && isMine(entry, meId);
  const canSeeReplies = entry !== undefined && canReadReplies(entry);
  const repliesQuery = useReplies(id, canSeeReplies);
  const replyAllowed = entry !== undefined && canReply(entry, meId);

  const attachmentsQuery = useEntryAttachments(id, entry !== undefined);

  const [replyBody, setReplyBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [privateModal, setPrivateModal] = useState(false);

  const markedIdRef = useRef<string | null>(null);

  // 同一路由换 id 时组件不会重新挂载：把上一条日记的瞬时状态清掉，
  // 否则回应草稿 / 菜单 / 错误会跟着串到下一篇。
  useEffect(() => {
    setReplyBody("");
    setError(null);
    setMenuOpen(false);
  }, [id]);

  // 对方读我的共享日记 → 打一次已读。作者本人 / 私密日记都不会走到这里。
  // 按 id 记，而不是一个布尔 ref —— 否则详情之间直接跳转时第二篇不会被标记。
  useEffect(() => {
    if (entry === undefined || user === null) return;
    if (isMine(entry, user.id)) return;
    if (entry.visibility !== "shared") return;
    if (markedIdRef.current === entry.id) return;
    markedIdRef.current = entry.id;
    void api.markRead(entry.id).catch(() => {
      // 打点失败不该影响阅读 —— 回执是「已经发生的好消息」，失败就下次再说。
    });
  }, [entry, user]);

  const createReply = useMutation({
    mutationFn: (text: string) => api.createReply(id as string, text),
    onSuccess: () => {
      setReplyBody("");
      void queryClient.invalidateQueries({ queryKey: ["replies", id] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const deleteReply = useMutation({
    mutationFn: (replyId: string) => api.deleteReply(replyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["replies", id] });
      toast.show("回应已删除");
    },
    onError: (e) => setError(errorMessage(e)),
  });

  async function confirmPrivate() {
    if (entry === undefined) return;
    try {
      const updated = await api.setEntryVisibility(entry.id, "private");
      queryClient.setQueryData(["entry", entry.id], updated);
      setPrivateModal(false);
      toast.show("已改为仅自己可见");
    } catch (e) {
      setError(errorMessage(e));
      setPrivateModal(false);
    }
  }

  async function makeShared() {
    if (entry === undefined) return;
    try {
      const updated = await api.setEntryVisibility(entry.id, "shared");
      queryClient.setQueryData(["entry", entry.id], updated);
      setMenuOpen(false);
      toast.show("已改为共享");
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  if (user === null) return null;

  const notFound =
    entryQuery.isError &&
    entryQuery.error instanceof ApiError &&
    entryQuery.error.status === 404;

  const attachments =
    attachmentsQuery.data !== undefined && "attachments" in attachmentsQuery.data
      ? attachmentsQuery.data.attachments
      : [];

  return (
    <>
      <NavBar
        title="日记详情"
        back
        center
        onBack={() => navigate(-1)}
        right={
          mine && entry !== undefined ? (
            <div className="menu-wrap">
              <button
                type="button"
                className="icon-btn"
                aria-label="更多"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <MoreIcon size={20} />
              </button>
              {menuOpen && (
                <div className="menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => navigate(`/entries/${entry.id}/edit`)}
                  >
                    <PencilIcon size={16} />
                    编辑
                  </button>
                  {entry.visibility === "shared" ? (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        setPrivateModal(true);
                      }}
                    >
                      <LockIcon size={16} />
                      改为仅自己可见
                    </button>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void makeShared()}
                    >
                      <ShareIcon size={16} />
                      改为共享给 {partner}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : undefined
        }
      />

      <div className="page">
        {entryQuery.isLoading && <Loading />}
        {notFound && (
          <div className="empty">
            <div className="em-t">读不到这篇日记</div>
            <div className="em-d">
              它可能已被删除、改为私密，或者根本不存在。为了不让「存在性」泄露，
              这里对这三种情况给出同样的回答。
            </div>
            <Link to="/" className="btn btn-ghost btn-sm">
              回到首页
            </Link>
          </div>
        )}
        {entryQuery.isError && !notFound && (
          <QueryError
            error={entryQuery.error}
            onRetry={() => void entryQuery.refetch()}
          />
        )}

        {entry !== undefined && (
          <>
            {error !== null && <div className="banner banner-error">{error}</div>}

            <div className="author-row">
              <Avatar
                name={entry.authorUsername}
                variant={mine ? "me" : "ta"}
                size={40}
              />
              <div className="author-info">
                <span className="n">
                  {mine ? "我" : entry.authorUsername}
                </span>
                <span className="t mono">
                  {formatDateTime(entry.writtenAt)}
                </span>
              </div>
              <span
                className={
                  entry.visibility === "shared"
                    ? "badge badge-shared"
                    : "badge badge-private"
                }
              >
                {entry.visibility === "shared" ? "已共享" : "仅自己可见"}
              </span>
            </div>

            {entry.title.trim() !== "" && (
              <h1 className="detail-title">{entry.title}</h1>
            )}
            {entry.body.trim() !== "" && <LongBody text={entry.body} />}
            {entry.mood !== null && (
              <span className="mood-pill">{entry.mood}</span>
            )}

            {attachments.length > 0 && (
              <div className="photo-grid">
                {attachments.map((a) => (
                  <img
                    key={a.id}
                    className="photo"
                    src={a.url}
                    alt="日记里的照片"
                    loading="lazy"
                  />
                ))}
              </div>
            )}

            {entry.visibility === "private" && (
              <div className="lock-hint">
                <LockIcon size={18} style={{ color: "var(--steel)" }} />
                <div className="c">
                  <span className="t">这条通道对任何人都无效，包括 {partner}</span>
                  <span className="d">
                    只有你读得到。改成共享后，{partner}才能看到。
                  </span>
                </div>
              </div>
            )}

            {/* 已读回执与回应是两个独立区域（spec §5.4.4 AC） */}
            {showsReadReceipt(entry, meId) && (
              <div className="row" style={{ gap: 6 }}>
                <CheckIcon size={16} style={{ color: "var(--success)" }} />
                <span className="small" style={{ color: "var(--success)" }}>
                  {partner} 已读
                </span>
              </div>
            )}

            {canSeeReplies && (
              <section className="reply-box">
                <div className="reply-head">
                  <ReplyIcon size={16} style={{ color: "var(--charcoal)" }} />
                  <h3>{mine ? `${partner} 的回应` : "我的回应"}</h3>
                </div>

                {repliesQuery.isLoading && (
                  <div className="small ghost-text">加载回应中…</div>
                )}
                {repliesQuery.isError && (
                  <div className="small ghost-text">回应暂时读不到。</div>
                )}
                {repliesQuery.data !== undefined &&
                  repliesQuery.data.replies.length === 0 && (
                    <div className="small ghost-text">
                      {mine ? `${partner} 还没有回应` : "还没有回应"}
                    </div>
                  )}

                {repliesQuery.data?.replies.map((reply) => (
                  <div key={reply.id} className="reply-item">
                    <div className="reply-au">
                      <Avatar
                        name={reply.authorUsername}
                        variant={reply.authorId === meId ? "me" : "ta"}
                        size={28}
                      />
                      <span className="n">
                        {reply.authorId === meId ? "我" : reply.authorUsername}
                      </span>
                      <span className="t mono">
                        {formatTime(reply.createdAt)}
                      </span>
                      {canDeleteReply(reply, meId) && (
                        <button
                          type="button"
                          className="icon-btn"
                          style={{ width: 28, height: 28 }}
                          aria-label="删除这条回应"
                          disabled={deleteReply.isPending}
                          onClick={() => deleteReply.mutate(reply.id)}
                        >
                          <TrashIcon size={15} />
                        </button>
                      )}
                    </div>
                    <div className="reply-tx">{reply.body}</div>
                  </div>
                ))}

                {replyAllowed && (
                  <>
                    <div className="quick-row">
                      {REPLY_PRESETS.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          className="quick"
                          onClick={() => setReplyBody(preset)}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                    <form
                      className="row"
                      style={{ gap: 8 }}
                      onSubmit={(e: FormEvent) => {
                        e.preventDefault();
                        const text = replyBody.trim();
                        if (text === "") return;
                        createReply.mutate(text);
                      }}
                    >
                      <label className="comment-input">
                        <input
                          value={replyBody}
                          onChange={(e) => setReplyBody(e.target.value)}
                          placeholder={`回 ${entry.authorUsername} 一句…`}
                          maxLength={2000}
                        />
                      </label>
                      <button
                        type="submit"
                        className="send-btn"
                        aria-label="发送回应"
                        disabled={createReply.isPending || replyBody.trim() === ""}
                      >
                        <SendIcon size={19} style={{ color: "#fff" }} />
                      </button>
                    </form>
                  </>
                )}
              </section>
            )}
          </>
        )}
      </div>

      {privateModal && entry !== undefined && (
        <VisibilityPrivateModal
          partnerName={partner}
          replyCount={
            repliesQuery.data !== undefined
              ? repliesQuery.data.replies.length
              : null
          }
          onCancel={() => setPrivateModal(false)}
          onConfirm={() => void confirmPrivate()}
        />
      )}
    </>
  );
}
